import type { Frame } from './types';

export type FrameStatus = 'loading' | 'ready' | 'error';

/** A fully-decoded, render-ready frame plus its measured memory footprint. */
export interface DecodedFrame {
  frame: Frame;
  colors: Float32Array;
  byteSize: number;
}

/** Produces a decoded frame (fetch → parse → color → measure). Injected for testability. */
export type FrameDecoder = (index: number, signal: AbortSignal) => Promise<DecodedFrame>;

export interface FrameCacheOptions {
  limitMb: number;
  frameCount: number;
  /** Prefetch neighbors within ±radius of the requested frame. Default 1. */
  windowRadius?: number;
  /** Fraction of the budget that triggers a soft warning. Default 0.8. */
  softWarnRatio?: number;
  decoder: FrameDecoder;
}

/** Immutable metrics snapshot consumed by the UI via useSyncExternalStore. */
export interface CacheMetrics {
  usageBytes: number;
  usageMb: number;
  limitMb: number;
  ratio: number;
  warn: boolean;
  over: boolean;
  entryCount: number;
  readyIndices: number[];
  loadingIndices: number[];
  hits: number;
  misses: number;
}

interface CacheEntry {
  index: number;
  status: FrameStatus;
  decoded?: DecodedFrame;
  byteSize: number;
  /** Logical clock value at last access — drives LRU eviction. */
  lastAccess: number;
  error?: string;
  promise?: Promise<DecodedFrame>;
  controller?: AbortController;
}

const MB = 1024 * 1024;

/**
 * Time-based (LRU) frame cache with a hard byte budget.
 *
 * - `getFrame` — sync accessor; records a hit/miss and bumps LRU.
 * - `loadFrame` — async ensure-loaded + prefetch the sliding window.
 * - Eviction — before inserting a frame of `newSize`, while
 *   `usage + newSize > limit`, evict the least-recently-accessed resident
 *   frame (never the one being inserted).
 * - The sliding window is the *prefetch* policy; LRU is the *eviction* policy.
 */
export class FrameCache {
  private readonly entries = new Map<number, CacheEntry>();
  private readonly listeners = new Set<() => void>();
  private readonly limitBytes: number;
  private readonly limitMb: number;
  private readonly frameCount: number;
  private readonly windowRadius: number;
  private readonly softWarnRatio: number;
  private readonly decoder: FrameDecoder;

  private clock = 0;
  private usageBytes = 0;
  private hits = 0;
  private misses = 0;
  private warned = false;
  private snapshot: CacheMetrics;

  constructor(opts: FrameCacheOptions) {
    this.limitMb = opts.limitMb;
    this.limitBytes = opts.limitMb * MB;
    this.frameCount = opts.frameCount;
    this.windowRadius = opts.windowRadius ?? 1;
    this.softWarnRatio = opts.softWarnRatio ?? 0.8;
    this.decoder = opts.decoder;
    this.snapshot = this.buildSnapshot();
  }

  // --- External store interface (for useSyncExternalStore) ---

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): CacheMetrics => this.snapshot;

  // --- Public cache API ---

  /** Sync lookup: returns the decoded frame if resident, else undefined. Records a hit/miss. */
  getFrame(index: number): DecodedFrame | undefined {
    const entry = this.entries.get(index);
    if (entry?.status === 'ready' && entry.decoded) {
      entry.lastAccess = ++this.clock;
      this.hits++;
      this.emit();
      return entry.decoded;
    }
    this.misses++;
    this.emit();
    return undefined;
  }

  /** Ensure `index` is loaded (fetch on miss), then prefetch its window. */
  async loadFrame(index: number): Promise<DecodedFrame> {
    const decoded = await this.ensure(index);
    this.prefetch(index);
    return decoded;
  }

  /** Warm the sliding window around `center` without recording stats. */
  prefetch(center: number): void {
    for (let d = 1; d <= this.windowRadius; d++) {
      for (const i of [center - d, center + d]) {
        if (i < 0 || i >= this.frameCount) continue;
        const e = this.entries.get(i);
        if (e && (e.status === 'ready' || e.status === 'loading')) continue;
        void this.ensure(i).catch(() => undefined); // best-effort
      }
    }
  }

  // --- Internals ---

  /** Load-or-dedup a single frame into the cache. Does not record hit/miss. */
  private ensure(index: number): Promise<DecodedFrame> {
    const existing = this.entries.get(index);
    if (existing?.status === 'ready' && existing.decoded) {
      existing.lastAccess = ++this.clock;
      return Promise.resolve(existing.decoded);
    }
    if (existing?.status === 'loading' && existing.promise) {
      existing.lastAccess = ++this.clock;
      return existing.promise; // coalesce concurrent requests
    }

    const controller = new AbortController();
    const entry: CacheEntry = {
      index,
      status: 'loading',
      byteSize: 0,
      lastAccess: ++this.clock,
      controller,
    };

    entry.promise = this.decoder(index, controller.signal)
      .then((decoded) => {
        // A newer request may have replaced/evicted this entry mid-flight.
        if (this.entries.get(index) !== entry) return decoded;
        this.evictToFit(decoded.byteSize, index);
        entry.status = 'ready';
        entry.decoded = decoded;
        entry.byteSize = decoded.byteSize;
        this.usageBytes += decoded.byteSize;
        this.checkSoftWarn();
        this.emit();
        return decoded;
      })
      .catch((err: unknown) => {
        if (this.entries.get(index) === entry) {
          entry.status = 'error';
          entry.error = String(err);
          this.emit();
        }
        throw err;
      });

    this.entries.set(index, entry);
    this.emit();
    return entry.promise;
  }

  /** Evict LRU resident frames until `newSize` fits (never evicts `protectIndex`). */
  private evictToFit(newSize: number, protectIndex: number): void {
    while (this.usageBytes + newSize > this.limitBytes) {
      const victim = this.pickLruVictim(protectIndex);
      if (victim === null) break; // only the protected frame remains → accept overage
      this.evict(victim);
    }
  }

  private pickLruVictim(protectIndex: number): number | null {
    let oldest: CacheEntry | null = null;
    for (const e of this.entries.values()) {
      if (e.index === protectIndex || e.status !== 'ready') continue;
      if (!oldest || e.lastAccess < oldest.lastAccess) oldest = e;
    }
    return oldest ? oldest.index : null;
  }

  private evict(index: number): void {
    const e = this.entries.get(index);
    if (!e) return;
    if (e.status === 'ready') this.usageBytes -= e.byteSize;
    e.controller?.abort();
    this.entries.delete(index);
    console.warn(
      `[FrameCache] evicted frame ${index} (LRU) → usage ${(this.usageBytes / MB).toFixed(1)} MB`,
    );
  }

  private checkSoftWarn(): void {
    const ratio = this.usageBytes / this.limitBytes;
    if (ratio >= this.softWarnRatio && !this.warned) {
      this.warned = true;
      console.warn(
        `[FrameCache] soft warning: usage ${(this.usageBytes / MB).toFixed(1)} MB ≥ ` +
          `${Math.round(this.softWarnRatio * 100)}% of ${this.limitMb} MB budget`,
      );
    } else if (ratio < this.softWarnRatio) {
      this.warned = false;
    }
  }

  private emit(): void {
    this.snapshot = this.buildSnapshot();
    this.listeners.forEach((l) => l());
  }

  private buildSnapshot(): CacheMetrics {
    const readyIndices: number[] = [];
    const loadingIndices: number[] = [];
    for (const e of this.entries.values()) {
      if (e.status === 'ready') readyIndices.push(e.index);
      else if (e.status === 'loading') loadingIndices.push(e.index);
    }
    readyIndices.sort((a, b) => a - b);
    loadingIndices.sort((a, b) => a - b);
    const ratio = this.limitBytes > 0 ? this.usageBytes / this.limitBytes : 0;
    return {
      usageBytes: this.usageBytes,
      usageMb: this.usageBytes / MB,
      limitMb: this.limitMb,
      ratio,
      warn: ratio >= this.softWarnRatio,
      over: this.usageBytes > this.limitBytes,
      entryCount: this.entries.size,
      readyIndices,
      loadingIndices,
      hits: this.hits,
      misses: this.misses,
    };
  }
}
